import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export interface BrandAnalysis {
  positioning_sentence?: string | null;
  real_audience?: string | null;
  cultural_territory_pair?: string | null;
  cultural_territory_text?: string | null;
  sub_category?: string | null;
  key_competitors?: string[] | null;
  // Legacy fields (kept for backwards-compat with older cached payloads)
  customer_portrait?: string | null;
  verbal_fingerprint?: string[] | null;
  cultural_territory?: string | null;
  tone?: string[] | null;
  core_themes?: string[] | null;
  community_signals?: string | null;
}

export interface BrandOnboardingDna {
  industries: string[];
  valuesRanked: { value: string; rank: number }[];
  positioningTier: string | null;
  genderFocus: string | null;
  ageMin: string | null;
  ageMax: string | null;
}

export interface BrandBranding {
  brandId: string | null;
  brandName: string | null;
  website: string | null;
  logo: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  colors: string[];
  fonts: string[];
  summary: string | null;
  scrapedAt: string | null;
  status: string | null;
  favicon: string | null;
  ogImage: string | null;
  socials: Record<string, string>;
  analysis: BrandAnalysis | null;
  communityActivity: string | null;
  dna: BrandOnboardingDna;
}

const EMPTY_DNA: BrandOnboardingDna = {
  industries: [], valuesRanked: [], positioningTier: null,
  genderFocus: null, ageMin: null, ageMax: null,
};

const EMPTY: BrandBranding = {
  brandId: null, brandName: null, website: null, logo: null,
  primaryColor: null, secondaryColor: null, accentColor: null,
  colors: [], fonts: [], summary: null, scrapedAt: null, status: null,
  favicon: null, ogImage: null, socials: {}, analysis: null,
  communityActivity: null, dna: EMPTY_DNA,
};

// Firecrawl branding payload shape is loose — defensive parsing.
type JsonRecord = Record<string, unknown>;

type BrandRow = {
  company_name: string | null;
  website: string | null;
  logo_url: string | null;
  scraped_summary: string | null;
  scraped_branding: unknown;
  scraped_at: string | null;
  scrape_status: string | null;
  stage: string | null;
  gender_focus: string | null;
  community_activity: string | null;
};

function asRecord(value: unknown): JsonRecord | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as JsonRecord : null;
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value;
    if (Array.isArray(value)) {
      const nested = firstString(...value);
      if (nested) return nested;
    }
    const record = asRecord(value);
    if (record) {
      const nested = firstString(record.url, record.src, record.href, record.logo, record.image);
      if (nested) return nested;
    }
  }
  return null;
}

function parse(brandId: string, row: BrandRow, dna: BrandOnboardingDna): BrandBranding {
  const b = asRecord(row.scraped_branding) ?? {};
  const images = asRecord(b.images);
  const nestedBranding = asRecord(b.branding);
  let logo = firstString(
    row.logo_url,
    b.logo,
    b.logoUrl,
    b.logo_url,
    images?.logo,
    images?.logoUrl,
    images?.logo_url,
    nestedBranding?.logo,
  );

  // Fallback: the site's icon from the brand's website hostname when the scrape missed the logo (Clearbit's logo API is discontinued).
  if (!logo && row.website) {
    try {
      const u = new URL(/^https?:\/\//i.test(row.website) ? row.website : `https://${row.website}`);
      const host = u.hostname.replace(/^www\./, "");
      if (host) logo = `https://www.google.com/s2/favicons?domain=${host}&sz=128`;
    } catch { /* ignore */ }
  }

  const normalizeHex = (c: unknown): string | null => {
    if (typeof c !== "string") return null;
    const t = c.trim();
    return /^#?[0-9a-f]{3,8}$/i.test(t) ? (t.startsWith("#") ? t : `#${t}`) : null;
  };

  const colorsSource = b.colors ?? b.palette;
  const colorsObj = colorsSource && !Array.isArray(colorsSource) && typeof colorsSource === "object"
    ? (colorsSource as Record<string, unknown>)
    : null;
  const rawColors: unknown[] = Array.isArray(colorsSource)
    ? colorsSource
    : colorsObj
    ? [colorsObj.primary, colorsObj.secondary, colorsObj.accent, colorsObj.link, colorsObj.textPrimary, colorsObj.text_primary, colorsObj.background]
    : [];
  const colors = rawColors
    .map((c) => (typeof c === "string" ? c : firstString(asRecord(c)?.hex, asRecord(c)?.value)))
    .map(normalizeHex)
    .filter((c): c is string => !!c);


  const fonts: string[] = Array.isArray(b.fonts)
    ? b.fonts.map((f) => (typeof f === "string" ? f : firstString(asRecord(f)?.name))).filter((f): f is string => !!f)
    : [];

  const analysisRaw = b?.analysis;
  const analysis: BrandAnalysis | null =
    analysisRaw && typeof analysisRaw === "object" ? analysisRaw : null;

  return {
    brandId,
    brandName: row?.company_name ?? null,
    website: row?.website ?? null,
    logo,
    primaryColor: firstString(b.primaryColor, b.primary_color, colorsObj ? normalizeHex(colorsObj.primary) : null, colors[0]),
    secondaryColor: firstString(b.secondaryColor, b.secondary_color, colorsObj ? normalizeHex(colorsObj.secondary) : null, colors[1]),
    accentColor: firstString(b.accentColor, b.accent_color, colorsObj ? normalizeHex(colorsObj.accent) : null, colors[2]),
    colors,
    fonts,
    summary: row?.scraped_summary ?? null,
    scrapedAt: row?.scraped_at ?? null,
    status: row?.scrape_status ?? null,
    favicon: firstString(b.favicon, b.faviconUrl, images?.favicon),
    ogImage: firstString(b.og_image, b.ogImage, images?.ogImage, images?.og_image),
    socials: Object.fromEntries(Object.entries(asRecord(b.socials) ?? {}).filter((entry): entry is [string, string] => typeof entry[1] === "string")),
    analysis,
    communityActivity: row?.community_activity ?? null,
    dna,
  };
}

export function useBrandBranding() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["brand-branding", user?.id],
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<BrandBranding> => {
      const { data: tm } = await supabase
        .from("team_members")
        .select("brand_id")
        .eq("user_id", user!.id)
        .limit(1)
        .maybeSingle();
      const brandId = tm?.brand_id;
      if (!brandId) return EMPTY;

      const [brandRes, indRes, valRes, ageRes] = await Promise.all([
        supabase
          .from("brands")
          .select("company_name, website, logo_url, scraped_summary, scraped_branding, scraped_at, scrape_status, stage, gender_focus, community_activity")
          .eq("id", brandId)
          .maybeSingle(),
        supabase.from("brand_industries").select("industry").eq("brand_id", brandId),
        supabase.from("brand_values").select("value, rank").eq("brand_id", brandId).order("rank"),
        supabase.from("brand_audience_ages").select("age_group, priority").eq("brand_id", brandId),
      ]);

      const row = brandRes.data;
      const dna: BrandOnboardingDna = {
        industries: (indRes.data || []).map((r) => r.industry),
        valuesRanked: (valRes.data || []).map((r) => ({ value: r.value, rank: r.rank })),
        positioningTier: row?.stage || null,
        genderFocus: row?.gender_focus || null,
        ageMin: (ageRes.data || []).find((a) => a.priority === "min")?.age_group ?? null,
        ageMax: (ageRes.data || []).find((a) => a.priority === "max")?.age_group ?? null,
      };

      if (!row) return { ...EMPTY, brandId, dna };
      return parse(brandId, row, dna);
    },
  });
}

export function brandInitials(name: string | null | undefined): string {
  if (!name?.trim()) return "B";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
